import {
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ObjectId } from 'bson';
import { BehaviorSubject, Observable, combineLatest, filter, of, take, timer } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { LANDING_URL, URL_CONSTANTS } from 'src/app/common/utils/url';
import { Session } from 'src/app/models/session';
import { SpaceFolder } from 'src/app/models/space-folder';
import { CannotDeleteSpaceReasonPipe } from 'src/app/pipes/cannot-delete-space-reason.pipe';
import { LocalStorageItemIDs, LocalStorageService } from 'src/app/services/local-storage.service';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { SpaceActionRulesService } from 'src/app/services/space-action-rules.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { SpaceFoldersService } from 'src/app/services/space-folders.service';
import { SpacesService } from 'src/app/services/spaces.service';
import { SpacesSwitcherService } from 'src/app/services/spaces-switcher.service';
import { SwitcherCallPresenceService } from 'src/app/services/switcher-call-presence.service';
import { UserService } from 'src/app/services/user.service';
import { ConfirmationModalComponent } from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { PencilLazyLoadingComponent } from 'src/app/standalones/components/pencil-lazy-loading/pencil-lazy-loading.component';
import { PencilMenuItemComponent } from 'src/app/standalones/components/pencil-menu-item/pencil-menu-item.component';
import { PnclMenuDividerComponent } from 'src/app/standalones/components/pncl-menu-divider/pncl-menu-divider.component';
import { PnclMenuHeaderComponent } from 'src/app/standalones/components/pncl-menu-header/pncl-menu-header.component';
import { TooltipDirective } from 'src/app/standalones/directives/pncl-tooltip.directive';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { NavSidebarItemComponent } from '../nav-sidebar-item/nav-sidebar-item.component';
import { NavSidebarSectionComponent } from '../nav-sidebar-section/nav-sidebar-section.component';

/** Rendered height of an app-nav-sidebar-item row (35px) plus the section's row gap (2px). */
const SWITCHER_ROW_HEIGHT = 37;

/** Rendered height of a section header (19px line box plus 4px of padding either side). */
const SECTION_HEADER_HEIGHT = 27;

const DEFAULT_SWITCHER_PAGE_SIZE = 7;

/**
 * Rows fetched beyond what fits on screen, so the list clears the lazy loader's lookahead
 * (--pencil-lazy-loading-lookahead in the stylesheet).
 */
const SPARE_PAGE_ROWS = 4;

/**
 * Failed page fetches tolerated before paging gives up. Enough that a blocked request retries on
 * the next scroll, few enough that a sustained outage isn't hammered.
 */
const MAX_CONSECUTIVE_PAGE_FAILURES = 3;

/**
 * Cycled through while a further page loads, so the wait has some life to it rather than a static
 * label. Translation keys, like every other string here — the list is what's cycled, not the
 * rendered text, so translators keep full control of each phrase.
 */
const LOADING_PHRASES = [
  'Rummaging…',
  'Gathering…',
  'Digging…',
  'Rounding up…',
  'Wrangling…',
  'Corralling…',
];

/** How long each phrase stays on screen. Slow enough to read, quick enough to feel alive. */
const LOADING_PHRASE_INTERVAL_MS = 1600;

/**
 * Well-known ids for the two sections the API returns as special cases. Drag-and-drop itself only
 * looks sections up by id — a folder is another entry in the list, not a new drop path. These ids
 * are the adapter to the pin/inbox endpoints; a folder's id is its own.
 */
const PINNED_SECTION_ID = 'pinned';
const INBOX_SECTION_ID = 'inbox';

/** Distinguishes a section's header drop list from the list of its rows. */
const HEADER_DROP_LIST_SUFFIX = '-header';

/**
 * How long a dragged space must hover a collapsed section before it springs open. Long enough to
 * read as intent rather than passing over, short enough not to feel like waiting — Finder and
 * VS Code sit in the same range.
 */
const SPRING_OPEN_DELAY_MS = 400;

/**
 * Must match .nav-sidebar-section__body's grid-template-rows transition — it's how long the drop
 * lists' geometry stays in motion after a section springs open.
 */
const SECTION_TRANSITION_MS = 180;

/** Which primary action a space row's menu offers, decided by the section holding the row. */
export enum SpaceRowMenu {
  Pin = 'pin',
  Unpin = 'unpin',
  RemoveFromSection = 'removeFromSection',
}

/** What a switcher drag is moving. Only a space springs collapsed sections open. */
export enum SwitcherDragKind {
  Space = 'space',
  Section = 'section',
}

/** A sidebar group of spaces. Pinned and folders persist order; the inbox does not. */
interface SwitcherSection {
  id: string;
  label: string;
  translateLabel: boolean;
  spaces: Session[];
  persistOrder: boolean;
  /** Renameable, deletable, and kept on screen while empty — unlike Pinned. */
  isFolder: boolean;
  /**
   * Seeded from storage when the section is built, then owned by the section component — this is
   * the initial value of a one-way binding, not a mirror of the live state.
   */
  expanded: boolean;
  /** Drop-list id of this section's header, which is how a collapsed section stays droppable. */
  headerDropListId: string;
}

/** Loaded switcher state for the template — one async so the view never calls functions. */
interface SwitcherView {
  /** Null while empty: Pinned is virtual, always first, and shown only when it holds spaces. */
  pinned: SwitcherSection | null;
  /** Reorderable among themselves, between Pinned and the inbox. */
  folders: SwitcherSection[];
  inbox: SwitcherSection;
  hasAnySpaces: boolean;
  /** Every section's drop-list id, so each list can accept a drag from any other. */
  dropListIds: string[];
  /** Ids of the spaces showing a call indicator. */
  spacesInCall: ReadonlySet<string>;
}

/**
 * The sidebar's Spaces Switcher: loads the user's spaces a page at a time, renders them as
 * droppable sections (pinned, the inbox, and later folders), highlights the open one, and pins,
 * unpins or deletes from each row's more menu.
 * Self-contained so the nav bar only has to decide *whether* to render it — everything else lives
 * here or in {@link SpacesSwitcherService}.
 */
@UntilDestroy()
@Component({
  selector: 'app-nav-spaces-switcher',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatMenuModule,
    TranslateModule,
    CannotDeleteSpaceReasonPipe,
    NavSidebarItemComponent,
    NavSidebarSectionComponent,
    PencilLazyLoadingComponent,
    PencilMenuItemComponent,
    PnclMenuDividerComponent,
    PnclMenuHeaderComponent,
    TooltipDirective,
  ],
  templateUrl: './nav-spaces-switcher.component.html',
  styleUrls: ['./nav-spaces-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavSpacesSwitcherComponent implements AfterViewInit, OnDestroy {
  /**
   * Null while the first page is loading; the section list once it resolves. Ordered sections
   * (pinned, later folders) come first, the inbox last. The null/array distinction drives the
   * loading vs loaded states in the template via the async pipe.
   */
  readonly sections$ = new BehaviorSubject<SwitcherSection[] | null>(null);

  /**
   * Sections a space can be moved into from its row menu — Pinned and every folder. The inbox is
   * absent by design: moving *out* is the row menu's "Unpin space" / "Remove from section".
   *
   * Pinned is listed even while empty, since it only renders once it holds something and this is
   * the one way to put the first space back into it without dragging.
   */
  readonly moveTargets$: Observable<SwitcherSection[]> = this.sections$.pipe(
    map((sections) => (sections ?? []).filter((section) => section.persistOrder)),
  );

  readonly renderMenuAsBottomSheet$ = this.screenSizeService.renderMenuAsBottomSheet$;

  /**
   * Sections the user has collapsed, persisted so the sidebar looks the same next visit. Collapsed
   * rather than expanded ids, so a section the user has never touched — including a folder they
   * just made — starts open.
   */
  private readonly collapsedSectionIds = new Set<string>();
  readonly switcherView$: Observable<SwitcherView | null> = combineLatest([
    this.sections$,
    this.switcherCallPresence.spacesInCall$,
  ]).pipe(
    map(([sections, spacesInCall]) => {
      if (!sections) {
        return null;
      }
      const inbox = sections.find((section) => section.id === INBOX_SECTION_ID);
      if (!inbox) {
        return null;
      }
      // Pinned exists only while it holds spaces: emptying it — by deleting the section, or by
      // unpinning its last row — is what removes it, and pinning again brings it back. A folder is
      // a thing the user made, so it stays put when empty (and droppable) until explicitly deleted.
      const pinned = sections.find((section) => section.id === PINNED_SECTION_ID);
      const visiblePinned = pinned?.spaces.length ? pinned : null;
      const folders = sections.filter((section) => section.isFolder);
      const visible = [...(visiblePinned ? [visiblePinned] : []), ...folders, inbox];

      return {
        pinned: visiblePinned,
        folders,
        inbox,
        hasAnySpaces: sections.some((section) => section.spaces.length > 0),
        // Headers included: dropping on one files the space into that section without the layout
        // shift that expanding it would cause.
        dropListIds: visible.flatMap((section) => [section.id, section.headerDropListId]),
        spacesInCall,
      };
    }),
  );
  /** True while a page of the spaces list (first page or a subsequent one) is loading. */
  readonly isFetching$ = new BehaviorSubject<boolean>(false);
  readonly hasMoreSpaces$ = new BehaviorSubject<boolean>(true);
  /** True while a space created from the section menu is in flight. */
  readonly creatingSpace$ = new BehaviorSubject<boolean>(false);
  readonly currentSpaceId$ = this.spacesSwitcherService.currentSpaceId$;

  /**
   * The phrase shown on the "loading more" row, rotating while a fetch is in flight. Starts at a
   * random phrase so a short wait isn't always the same word, and stops advancing between fetches
   * (the row is hidden then, and an idle interval would keep waking change detection).
   */
  readonly loadingPhrase$: Observable<string> = this.isFetching$.pipe(
    switchMap((isFetching) => {
      if (!isFetching) {
        return of(LOADING_PHRASES[0]);
      }
      const start = Math.floor(Math.random() * LOADING_PHRASES.length);
      // startWith, so the row has a phrase on the render that creates it — timer's own first
      // emission lands a tick later, by which time it has already shown an empty label.
      return timer(0, LOADING_PHRASE_INTERVAL_MS).pipe(
        map((tick) => LOADING_PHRASES[(start + tick) % LOADING_PHRASES.length]),
        startWith(LOADING_PHRASES[start]),
      );
    }),
  );

  /**
   * Fires whenever a click here navigates away. The nav bar owns what that means for the sidebar —
   * it dismisses the drawer while it overlays the content.
   */
  @Output() navigated = new EventEmitter<void>();

  // Recomputed from the room below this component right before the first page loads, so the
  // switcher starts with roughly a screenful of spaces instead of a fixed guess.
  private pageSize = DEFAULT_SWITCHER_PAGE_SIZE;
  private currentPage = 1;
  /** Set only by a successful first page — a failed one still populates sections$ with empties. */
  private firstPageLoaded = false;
  private consecutivePageFailures = 0;
  // Ids already rendered in either list. The pinned/regular split itself is guaranteed by the API;
  // this set only absorbs pagination skew from local mutations — a space unpinned locally, which a
  // later page fetch may return again because the skip/limit window shifted.
  private readonly loadedSpaceIds = new Set<string>();

  @ViewChildren(NavSidebarSectionComponent)
  private sectionComponents?: QueryList<NavSidebarSectionComponent>;

  /**
   * Set while a row is being dragged so the click that follows a drop doesn't also navigate
   * into that space.
   */
  private ignoreSwitcherItemClick = false;

  /** Where the space just landed, set by the drop handlers for onSwitcherDragEnded to read. */
  private landedSectionId = '';

  /** True only while a *space* is being dragged — reordering sections must not spring anything. */
  private readonly draggingSpace$ = new BehaviorSubject(false);
  /**
   * Sections spring-loading opened during the current drag, so the ones the space didn't land in
   * can be closed again rather than left hanging open.
   */
  private readonly springOpenedSectionIds = new Set<string>();
  private springTimer?: ReturnType<typeof setTimeout>;

  // Every switcher drop list, so their cached geometry can be refreshed when a spring-open shifts
  // the sidebar mid-drag. See refreshDropListGeometry.
  @ViewChildren(CdkDropList) private switcherDropLists?: QueryList<CdkDropList>;

  protected readonly IconTypes = IconTypes;
  protected readonly SwitcherDragKind = SwitcherDragKind;
  protected readonly SpaceRowMenu = SpaceRowMenu;

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private router: Router,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private screenSizeService: ScreenSizeService,
    private localStorageService: LocalStorageService,
    private notificationToasterService: NotificationToasterService,
    private spaceActionRules: SpaceActionRulesService,
    private sharedDataService: SessionSharedDataService,
    private spaceFoldersService: SpaceFoldersService,
    private spacesService: SpacesService,
    private spacesSwitcherService: SpacesSwitcherService,
    private switcherCallPresence: SwitcherCallPresenceService,
    private translateService: TranslateService,
    private userService: UserService,
  ) {
    this.loadCollapsedSections();
  }

  // Deferred to here rather than ngOnInit so calculatePageSize can measure a rendered view.
  ngAfterViewInit(): void {
    // The list request is authenticated, so wait for the user to resolve before the first page.
    this.userService.user
      .pipe(
        filter((res: any) => !!res?.user),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(() => this.loadSpaces());
  }

  ngOnDestroy(): void {
    // Not covered by @UntilDestroy — releasing the presence rooms is explicit.
    this.switcherCallPresence.untrackAll();
  }

  goToSpace(id: string): void {
    this.navigated.emit();
    this.spacesSwitcherService.switchToSpace(id);
  }

  onSwitcherSpaceClick(id: string): void {
    if (this.ignoreSwitcherItemClick) {
      return;
    }
    this.goToSpace(id);
  }

  onSwitcherDragStarted(kind: SwitcherDragKind = SwitcherDragKind.Space): void {
    this.ignoreSwitcherItemClick = true;
    this.draggingSpace$.next(kind === SwitcherDragKind.Space);
  }

  /**
   * Ends a drag: stop any pending spring-open, and close the sections spring-loading opened that the
   * space didn't end up in. Without this, a drag that wanders across several leaves them all hanging
   * open. Sections the user expanded themselves are never touched.
   *
   * Deferred a macrotask because CDK fires `ended` *before* `dropped`, so where the space landed
   * isn't known yet — the drop handlers set landedSectionId in between.
   */
  onSwitcherDragEnded(): void {
    this.clearSpringTimer();
    this.draggingSpace$.next(false);

    modifiedSetTimeout(() => {
      this.springOpenedSectionIds.forEach((id) => {
        if (id === this.landedSectionId) {
          // The space landed here, so the provisional open becomes a real one and is persisted.
          this.setSectionExpanded(id, true);
          return;
        }
        this.sectionComponentFor(id)?.setExpanded(false);
      });
      this.springOpenedSectionIds.clear();
      this.landedSectionId = '';
      this.ignoreSwitcherItemClick = false;
    }, 0);
  }

  /**
   * Spring-loaded sections: hovering a collapsed section mid-drag opens it after a beat, so a space
   * can be placed at a precise index instead of only appended.
   */
  onSectionHeaderEntered(section: SwitcherSection): void {
    this.clearSpringTimer();
    if (!this.draggingSpace$.value) {
      return;
    }
    const sectionComponent = this.sectionComponentFor(section.id);
    if (!sectionComponent || sectionComponent.expanded) {
      return;
    }

    // Armed inside the zone deliberately: CDK emits its drop-list events outside it, and
    // modifiedSetTimeout samples the zone when armed — so armed as-is the callback would run
    // out-of-zone and expand the section without anything re-rendering, leaving it to open only
    // once some later event drags a change detection pass along.
    this.ngZone.run(() => {
      this.springTimer = modifiedSetTimeout(() => {
        // Not persisted — a spring-open is provisional until the space actually lands here.
        sectionComponent.setExpanded(true, false);
        this.springOpenedSectionIds.add(section.id);
        // The section animates open, so the rows below it keep moving for the length of that
        // transition — one re-measure would be correct only for the frame it ran on.
        this.refreshDropListGeometryWhileAnimating();
      }, SPRING_OPEN_DELAY_MS);
    });
  }

  onSectionHeaderExited(): void {
    this.clearSpringTimer();
  }

  /**
   * Re-measures every switcher drop list after a spring-open changed the layout mid-drag.
   *
   * CDK caches each list's ClientRect once, when the drag starts, and only refreshes it on events it
   * knows about — entering a list, scrolling. Expanding a section moves every row below it, which
   * CDK has no way to learn about, so without this its hit-testing and sort thresholds keep pointing
   * at where the rows used to be: the pointer moves through the newly revealed rows and nothing
   * responds.
   *
   * `_cacheParentPositions` is exactly the right method and is private in CDK 14, with no public
   * equivalent — hence the cast and the optional call, so a future CDK that renames it degrades to
   * stale geometry rather than throwing mid-drag. If that happens, the supported fallback is to drop
   * spring-loading: dropping onto a collapsed section's header already works and shifts no layout.
   */
  private refreshDropListGeometry(): void {
    this.switcherDropLists?.forEach((list) => {
      const ref = list._dropListRef as unknown as {
        _cacheParentPositions?: () => void;
      };
      ref._cacheParentPositions?.();
    });
  }

  /**
   * Runs outside the Angular zone: this reads layout and mutates nothing the template binds to, so a
   * change detection pass per frame would be pure overhead in the middle of a drag.
   */
  private refreshDropListGeometryWhileAnimating(): void {
    this.ngZone.runOutsideAngular(() => {
      // A little past the transition so the final resting position is the last one measured.
      const until = performance.now() + SECTION_TRANSITION_MS + 50;
      const step = (): void => {
        this.refreshDropListGeometry();
        if (performance.now() < until) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    });
  }

  private clearSpringTimer(): void {
    if (this.springTimer) {
      clearTimeout(this.springTimer);
      this.springTimer = undefined;
    }
  }

  trackBySpaceId(_index: number, space: Session): string {
    return space._id;
  }

  trackBySectionId(_index: number, section: SwitcherSection): string {
    return section.id;
  }

  /**
   * Moves or reorders a space from a drop. Works for any number of sections — the lists are
   * looked up from the drop data, which is the section itself.
   *
   * Reordering inside a section that does not persist order (the inbox) is ignored. Everything
   * else is applied optimistically and then persisted.
   */
  dropSpace(event: CdkDragDrop<SwitcherSection>): void {
    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    }

    // Released between two sections rather than on one. CDK still names the last list entered as
    // the target, so without this the space files itself somewhere the pointer never was — leave it
    // where it started instead.
    if (!event.isPointerOverContainer) {
      return;
    }

    const previous = this.sections$.getValue() ?? [];
    const sections = this.cloneSections();
    const from = sections.find((section) => section.id === event.previousContainer.data?.id);
    const to = sections.find((section) => section.id === event.container.data?.id);
    if (!from || !to) {
      return;
    }

    if (from.id === to.id) {
      if (!to.persistOrder) {
        return;
      }
      moveItemInArray(to.spaces, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(from.spaces, to.spaces, event.previousIndex, event.currentIndex);
    }

    this.landedSectionId = to.id;
    this.sections$.next(sections);
    this.persistSections(previous);
  }

  /**
   * Adds an empty folder above the inbox and drops straight into renaming it.
   *
   * The id is generated here rather than awaited from the API so the section can be rendered,
   * renamed and dropped into immediately; SpaceFoldersService queues the writes behind the create,
   * so they can't overtake it.
   */
  createFolder(): void {
    this.closeMenus();

    const section = this.folderSection(
      new ObjectId().toHexString(),
      this.translateService.instant('New folder'),
    );
    this.spaceFoldersService.createFolder(section.id, section.label);

    const sections = this.cloneSections();
    sections.splice(this.indexOfInbox(sections), 0, section);
    this.sections$.next(sections);

    // Deferred so the new section is in the DOM before we ask it to focus its title.
    modifiedSetTimeout(() => this.sectionComponentFor(section.id)?.startRename(), 0);
  }

  startSectionRename(section: SwitcherSection): void {
    this.closeMenus();
    this.sectionComponentFor(section.id)?.startRename();
  }

  renameSection(section: SwitcherSection, title: string): void {
    if (!section.isFolder || title === section.label) {
      return;
    }

    this.spaceFoldersService.renameFolder(section.id, title);
    this.sections$.next(
      this.cloneSections().map((item) =>
        item.id === section.id ? { ...item, label: title } : item,
      ),
    );
  }

  /**
   * Reorders the folders. Only their relative order is sent — the server generates the sort key
   * between the two neighbours the folder landed among, so the move is one write on one document.
   */
  dropSection(event: CdkDragDrop<SwitcherSection[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const folders = [...event.container.data];
    moveItemInArray(folders, event.previousIndex, event.currentIndex);

    const moved = folders[event.currentIndex];
    this.spaceFoldersService.moveFolder(
      moved.id,
      folders[event.currentIndex - 1]?.id ?? null,
      folders[event.currentIndex + 1]?.id ?? null,
    );

    // Rebuilt rather than spliced, so Pinned stays first and the inbox last whatever moved.
    const sections = this.cloneSections();
    this.sections$.next([
      ...sections.filter((section) => section.id === PINNED_SECTION_ID),
      ...folders.map((folder) => sections.find((section) => section.id === folder.id) ?? folder),
      ...sections.filter((section) => section.id === INBOX_SECTION_ID),
    ]);
  }

  /**
   * A space dropped on a section's header row, which is how a collapsed section is reachable at all
   * — its body is zero-height and inert. Appends to the end, since a collapsed section shows no
   * rows to aim between.
   */
  dropSpaceOnSectionHeader(section: SwitcherSection, event: CdkDragDrop<unknown>): void {
    const space = event.item.data as Session | undefined;
    // Same as dropSpace: a release in the gap under the header still reports the header as target.
    if (!space || !event.isPointerOverContainer) {
      return;
    }

    const previous = this.sections$.getValue() ?? [];
    const sections = this.withoutSpace(this.cloneSections(), space);
    const target = sections.find((item) => item.id === section.id);
    if (!target) {
      return;
    }

    target.spaces = [...target.spaces, space];
    this.landedSectionId = target.id;
    this.sections$.next(sections);
    this.persistSections(previous);
  }

  /** Opens the full Spaces page. */
  viewAllSpaces(): void {
    this.navigated.emit();
    this.router.navigate([`/${URL_CONSTANTS.SPACES}`]);
  }

  /** Creates a space and opens it, without going via the Spaces page first. */
  createSpace(): void {
    if (this.creatingSpace$.value) {
      return;
    }
    this.creatingSpace$.next(true);

    this.spacesService
      .createSpace('')
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (space) => {
          this.creatingSpace$.next(false);
          this.sharedDataService.populateData([space]);
          const newSpace = new Session(space);
          this.loadedSpaceIds.add(newSpace._id);
          const sections = this.cloneSections();
          const inbox = sections.find((section) => section.id === INBOX_SECTION_ID);
          if (inbox) {
            inbox.spaces = [newSpace, ...inbox.spaces];
            this.sections$.next(sections);
            this.trackCallPresence();
          }
          this.goToSpace(newSpace._id);
        },
        error: () => this.creatingSpace$.next(false),
      });
  }

  /**
   * Pins a space, moving it out of the regular list and to the top of the Pinned section.
   *
   * The move is applied optimistically and the write is fired off in the background, so the row
   * jumps the instant it's clicked instead of after a round trip. A failed write only leaves the
   * sidebar out of sync until the next load, which re-reads the real pin state from the API.
   */
  pinSpace(space: Session): void {
    this.closeMenus();

    const previous = this.sections$.getValue() ?? [];
    const sections = this.withoutSpace(this.cloneSections(), space);
    const pinned = sections.find((section) => section.id === PINNED_SECTION_ID);
    if (pinned) {
      // Newest pin first, matching how the API returns the list.
      pinned.spaces = [space, ...pinned.spaces];
    }
    this.sections$.next(sections);
    this.persistSections(previous);
  }

  /**
   * Files a space into an ordered section from the row menu — the same outcome as dragging it
   * there, for when dragging is awkward or the target is scrolled out of view.
   */
  moveSpaceToSection(space: Session, section: SwitcherSection): void {
    this.closeMenus();

    const previous = this.sections$.getValue() ?? [];
    const sections = this.withoutSpace(this.cloneSections(), space);
    const target = sections.find((item) => item.id === section.id);
    if (!target) {
      return;
    }

    // Newest first, matching where pinSpace puts a freshly pinned space.
    target.spaces = [space, ...target.spaces];
    this.sections$.next(sections);
    this.persistSections(previous);
  }

  /** Opens a row menu's submenu. Mirrors the board settings menu's own submenu handling. */
  openSubMenu(event: Event, trigger: MatMenuTrigger): void {
    event.preventDefault();
    event.stopPropagation();
    trigger.openMenu();
  }

  /** Unpins a space, returning it to the inbox. */
  unpinSpace(space: Session): void {
    this.moveSpaceToInbox(space);
  }

  /**
   * Takes a space out of the folder holding it, returning it to the inbox. The same move as
   * unpinning — only the wording differs, since "unpin" means nothing for a folder.
   */
  removeSpaceFromSection(space: Session): void {
    this.moveSpaceToInbox(space);
  }

  /**
   * Pulls a space out of whichever ordered section holds it and slots it back into the inbox by
   * recency. Optimistic in the same way as pinSpace.
   */
  private moveSpaceToInbox(space: Session): void {
    this.closeMenus();

    const previous = this.sections$.getValue() ?? [];
    const sections = this.withoutSpace(this.cloneSections(), space);
    const inbox = sections.find((section) => section.id === INBOX_SECTION_ID);
    if (inbox) {
      inbox.spaces = this.insertSpaceByRecency(inbox.spaces, space);
    }
    this.sections$.next(sections);
    this.persistSections(previous);
  }

  /**
   * Empties an ordered section, moving its spaces to the top of the inbox in the same order.
   * Optimistic like unpinSpace — the rows move immediately and the write is fired in the background.
   */
  deleteSection(section: SwitcherSection): void {
    if (!section.persistOrder) {
      return;
    }

    this.closeMenus();

    const sectionName = section.translateLabel
      ? this.translateService.instant(section.label)
      : section.label;
    const firstLine = this.translateService.instant(
      'Spaces added to <b>{{sectionName}}</b> will move back to the Space list.',
      { sectionName },
    );
    const secondLine = this.translateService.instant(
      "You won't be removed from these Spaces when you delete this section.",
    );

    this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          title: this.translateService.instant('Delete section?'),
          description: `${firstLine}<br><br>${secondLine}`,
          confirmButtonConfig: {
            icon: 'delete_outline',
            iconType: 'mat-icon',
            label: this.translateService.instant('Delete section'),
            danger: true,
          },
          cancelButtonConfig: {
            icon: 'close',
            iconType: 'mat-icon',
            label: this.translateService.instant('Cancel'),
          },
        },
      })
      .afterClosed()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((output: 'confirm' | 'cancel') => {
        if (output !== 'confirm') {
          return;
        }
        this.emptySectionIntoInbox(section.id);
      });
  }

  /**
   * Same confirmation as Space Details. The row is removed the instant it's confirmed — the write
   * is fired off in the background, the same way as pin/unpin. If this was the open space, we
   * switch to the next one in its list (or the first space in the next section when it was last
   * in its own). A failed write only leaves the sidebar out of sync until the next load.
   */
  deleteSpace(space: Session): void {
    if (this.spaceActionRules.getCurrentUserCannotDeleteSpaceReason(space)) {
      return;
    }

    this.closeMenus();

    this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          title: this.translateService.instant('Delete Space?'),
          description: this.translateService.instant(
            'Are you sure you want to delete this Space? This cannot be undone.',
          ),
          confirmButtonConfig: {
            icon: 'delete_outline',
            iconType: 'mat-icon',
            label: this.translateService.instant('Delete Space'),
            danger: true,
          },
          cancelButtonConfig: {
            icon: 'close',
            iconType: 'mat-icon',
            label: this.translateService.instant('Cancel'),
          },
        },
      })
      .afterClosed()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((output: 'confirm' | 'cancel') => {
        if (output !== 'confirm') {
          return;
        }

        this.removeDeletedSpace(space);
        this.persistDeletedSpace(space);
      });
  }

  /** Resets paging state and loads the first page. */
  private loadSpaces(): void {
    this.currentPage = 1;
    this.firstPageLoaded = false;
    this.consecutivePageFailures = 0;
    this.hasMoreSpaces$.next(true);
    this.loadedSpaceIds.clear();
    this.pageSize = this.calculatePageSize();
    this.fetchPage(1);
  }

  /** Fetches the next page of regular spaces. The pinned list arrived complete with the first. */
  loadMore(): void {
    if (this.isFetching$.value || !this.hasMoreSpaces$.value) {
      return;
    }
    // Re-fetches page 1 when it never landed, rather than paging on top of a list that isn't there.
    this.fetchPage(this.firstPageLoaded ? this.currentPage + 1 : 1);
  }

  private fetchPage(pageNumber: number): void {
    this.isFetching$.next(true);
    this.spacesService
      .getSwitcherSpacesList(pageNumber, this.pageSize)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.currentPage = pageNumber;
          this.firstPageLoaded = true;
          this.consecutivePageFailures = 0;
          this.hasMoreSpaces$.next(!!res?.hasMoreSpaces);

          // Seeds sessionAuthData from each space's metadata.auth_token. Presence room joins read
          // that map and silently skip any space missing from it, so without this the call
          // indicator subscribes to nothing and never lights up.
          this.sharedDataService.populateData([
            ...(res?.sessions ?? []),
            ...(res?.pinnedSessions ?? []),
            ...(res?.folderSessions ?? []),
          ]);

          if (pageNumber === 1) {
            const sections = this.emptySections(res?.folders);
            // Pinned and folders before the inbox, so a space in two places at once is kept by the
            // section that owns it — takeUnseenSpaces drops the later copy.
            this.fillSection(sections, PINNED_SECTION_ID, res?.pinnedSessions);
            this.fillFolderSections(sections, res?.folders, res?.folderSessions);
            this.fillSection(sections, INBOX_SECTION_ID, res?.sessions);
            this.sections$.next(sections);
          } else {
            this.appendToSection(INBOX_SECTION_ID, res?.sessions);
          }

          // Last, so the lazy loader re-observes against the rows this page just added.
          this.trackCallPresence();
          this.isFetching$.next(false);
        },
        error: () => {
          this.consecutivePageFailures += 1;

          // Stopping here also tears down the lazy loader's observer, and only a fresh load re-arms
          // it — so giving up on the first failure ends paging for as long as the sidebar stays
          // mounted. Retrying instead needs the cap: the observer reconnects onto an anchor already
          // in view and fires straight away, which against a sustained outage would spin.
          if (this.consecutivePageFailures >= MAX_CONSECUTIVE_PAGE_FAILURES) {
            this.hasMoreSpaces$.next(false);
          }

          // Resolve the loading state on a failed first page so the UI exits it cleanly.
          if (pageNumber === 1) {
            this.sections$.next(this.emptySections());
          }

          this.isFetching$.next(false);
        },
      });
  }

  /** Reports every rendered space to the call-presence tracker, which ignores ones it already has. */
  private trackCallPresence(): void {
    this.switcherCallPresence.track(
      (this.sections$.getValue() ?? []).flatMap((section) => section.spaces),
    );
  }

  /** Appends a freshly fetched page to a section, skipping spaces already rendered. */
  private appendToSection(sectionId: string, fetched: Session[] | undefined): void {
    const sections = this.cloneSections();
    this.fillSection(sections, sectionId, fetched);
    this.sections$.next(sections);
  }

  /** Only the sections that differ from the default (expanded) are stored. */
  setSectionExpanded(sectionId: string, expanded: boolean): void {
    if (expanded) {
      this.collapsedSectionIds.delete(sectionId);
    } else {
      this.collapsedSectionIds.add(sectionId);
    }
    this.publishSectionExpansion();
  }

  private publishSectionExpansion(): void {
    this.localStorageService.setItem(
      LocalStorageItemIDs.SWITCHER_COLLAPSED_SECTIONS,
      JSON.stringify([...this.collapsedSectionIds]),
    );
  }

  private loadCollapsedSections(): void {
    try {
      const stored = JSON.parse(
        this.localStorageService.getItem(LocalStorageItemIDs.SWITCHER_COLLAPSED_SECTIONS) || '[]',
      );
      if (Array.isArray(stored)) {
        stored
          .filter((id): id is string => typeof id === 'string')
          .forEach((id) => this.collapsedSectionIds.add(id));
      }
    } catch {
      // Hand-edited or half-written storage shouldn't stop the sidebar rendering.
    }
  }

  private folderSection(id: string, title: string): SwitcherSection {
    return {
      id,
      label: title,
      // Folder names are the user's own words, so they're rendered as typed.
      translateLabel: false,
      spaces: [],
      persistOrder: true,
      isFolder: true,
      expanded: !this.collapsedSectionIds.has(id),
      headerDropListId: `${id}${HEADER_DROP_LIST_SUFFIX}`,
    };
  }

  /**
   * Slots each folder's spaces into its section. The API sends membership as ids and the spaces
   * themselves in one flat list, so the ids are what carry the order.
   */
  private fillFolderSections(
    sections: SwitcherSection[],
    folders: SpaceFolder[] | undefined,
    folderSessions: Session[] | undefined,
  ): void {
    const sessionsById = new Map((folderSessions ?? []).map((space) => [space._id, space]));

    (folders ?? []).forEach((folder) => {
      const spaces = folder.spaceIds
        .map((id) => sessionsById.get(id))
        .filter((space): space is Session => !!space);
      this.fillSection(sections, folder._id, spaces);
    });
  }

  private fillSection(
    sections: SwitcherSection[],
    sectionId: string,
    fetched: Session[] | undefined,
  ): void {
    const section = sections.find((item) => item.id === sectionId);
    if (!section) {
      return;
    }
    section.spaces = [...section.spaces, ...this.takeUnseenSpaces(fetched)];
  }

  private takeUnseenSpaces(fetched: Session[] | undefined): Session[] {
    return (fetched ?? [])
      .map((space) => new Session(space))
      .filter((space) => {
        if (this.loadedSpaceIds.has(space._id)) {
          return false;
        }
        this.loadedSpaceIds.add(space._id);
        return true;
      });
  }

  /** Drops a deleted space from every section and switches away if it was the open one. */
  private removeDeletedSpace(space: Session): void {
    const wasCurrent = this.spacesSwitcherService.currentSpaceId === space._id;
    const nextSpace = wasCurrent ? this.nextSpaceAfterDelete(space) : undefined;
    const previous = this.sections$.getValue() ?? [];

    this.sections$.next(this.withoutSpace(this.cloneSections(), space));
    this.loadedSpaceIds.delete(space._id);
    this.persistSections(previous);

    if (wasCurrent) {
      if (nextSpace) {
        this.goToSpace(nextSpace._id);
      } else {
        this.navigated.emit();
        this.router.navigateByUrl(`/${LANDING_URL}`);
      }
    }
  }

  /**
   * The space to open after deleting the selected one, always the nearest surviving row: the one
   * below it, else the first in a following section, else the one above it, else the last row of
   * the nearest section above. Only when nothing is left do we leave for Home.
   */
  private nextSpaceAfterDelete(space: Session): Session | undefined {
    const sections = this.sections$.getValue() ?? [];
    const sectionIndex = sections.findIndex((section) =>
      section.spaces.some((item) => item._id === space._id),
    );
    if (sectionIndex === -1) {
      return undefined;
    }

    const section = sections[sectionIndex];
    const index = section.spaces.findIndex((item) => item._id === space._id);
    if (section.spaces[index + 1]) {
      return section.spaces[index + 1];
    }

    for (let i = sectionIndex + 1; i < sections.length; i++) {
      if (sections[i].spaces.length) {
        return sections[i].spaces[0];
      }
    }

    if (section.spaces[index - 1]) {
      return section.spaces[index - 1];
    }

    // Upwards from the section above, and its *last* row — the one that was directly above the
    // deleted one. Everything below was already ruled out by the loop above.
    for (let i = sectionIndex - 1; i >= 0; i--) {
      const spacesAbove = sections[i].spaces;
      if (spacesAbove.length) {
        return spacesAbove[spacesAbove.length - 1];
      }
    }

    return undefined;
  }

  /**
   * The UI has already dropped the row, so errors are swallowed here (the HTTP interceptor still
   * surfaces them): there's no local state left to roll back to, and the next load re-reads the
   * real list from the API.
   */
  private persistDeletedSpace(space: Session): void {
    this.spacesService
      .deleteSession(space._id)
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe((res) => {
        if (!res?.data) {
          return;
        }

        this.notificationToasterService.showDefaultSuccessNotification(
          this.translateService.instant('Your Space was successfully deleted.'),
          this.notificationToasterService.generateNotificationCode(),
        );
      });
  }

  /**
   * Moves every space in an ordered section to the top of the inbox, keeping their section order,
   * then persists.
   */
  private emptySectionIntoInbox(sectionId: string): void {
    const previous = this.sections$.getValue() ?? [];
    const sections = this.cloneSections();
    const from = sections.find((section) => section.id === sectionId);
    const inbox = sections.find((section) => section.id === INBOX_SECTION_ID);
    if (!from || !inbox) {
      return;
    }

    const moving = from.spaces;
    const movingIds = new Set(moving.map((space) => space._id));
    from.spaces = [];
    inbox.spaces = [...moving, ...inbox.spaces.filter((space) => !movingIds.has(space._id))];

    // A folder is the section itself, so deleting it removes the row rather than just emptying it.
    // The spaces it held are untouched — they're back in the inbox above.
    if (from.isFolder) {
      this.spaceFoldersService.deleteFolder(from.id);
      this.sections$.next(sections.filter((section) => section.id !== from.id));
      // Drop the stored collapse state too, or deleted folders accumulate in it indefinitely.
      this.collapsedSectionIds.delete(from.id);
      this.publishSectionExpansion();
      return;
    }

    this.sections$.next(sections);
    this.persistSections(previous);
  }

  /**
   * Slots a space into the regular list by recency (updatedAt descending), skipping a duplicate
   * if it's already there.
   */
  private insertSpaceByRecency(spaces: Session[], space: Session): Session[] {
    const without = spaces.filter((s) => s._id !== space._id);
    const insertAt = without.findIndex((s) => s.updatedAt < space.updatedAt);
    return insertAt === -1
      ? [...without, space]
      : [...without.slice(0, insertAt), space, ...without.slice(insertAt)];
  }

  private cloneSections(): SwitcherSection[] {
    return (this.sections$.getValue() ?? []).map((section) => ({
      ...section,
      spaces: [...section.spaces],
    }));
  }

  private withoutSpace(sections: SwitcherSection[], space: Session): SwitcherSection[] {
    return sections.map((section) => ({
      ...section,
      spaces: section.spaces.filter((item) => item._id !== space._id),
    }));
  }

  /**
   * The section list before any spaces are slotted into it: Pinned, then a section per folder in
   * the order the API returned them, then the inbox.
   */
  private emptySections(folders: SpaceFolder[] = []): SwitcherSection[] {
    return [
      {
        id: PINNED_SECTION_ID,
        label: 'Pinned',
        translateLabel: true,
        spaces: [],
        persistOrder: true,
        isFolder: false,
        expanded: !this.collapsedSectionIds.has(PINNED_SECTION_ID),
        headerDropListId: `${PINNED_SECTION_ID}${HEADER_DROP_LIST_SUFFIX}`,
      },
      ...folders.map((folder) => this.folderSection(folder._id, folder.title)),
      {
        id: INBOX_SECTION_ID,
        label: 'Spaces',
        translateLabel: true,
        spaces: [],
        persistOrder: false,
        isFolder: false,
        expanded: !this.collapsedSectionIds.has(INBOX_SECTION_ID),
        headerDropListId: `${INBOX_SECTION_ID}${HEADER_DROP_LIST_SUFFIX}`,
      },
    ];
  }

  /**
   * MatMenu closes itself on any click inside the panel, but the small-screen bottom sheet only
   * closes via its own X — so an action taken from it has to dismiss it.
   */
  private closeMenus(): void {
    this.bottomSheet._openedBottomSheetRef?.dismiss();
  }

  /**
   * Writes back every ordered section whose contents differ from `previous`. Each section owns its
   * own list, so a drag between two of them is two independent writes and an untouched section
   * costs nothing.
   *
   * The UI has already applied the change, so errors are swallowed here (the HTTP interceptor still
   * surfaces them): there's no local state left to roll back to, and the next load re-reads the
   * real list from the API.
   */
  private persistSections(previous: SwitcherSection[]): void {
    const previousSpaceIds = new Map(
      previous.map((section) => [section.id, this.spaceIdsOf(section).join()]),
    );

    (this.sections$.getValue() ?? [])
      .filter((section) => section.persistOrder)
      .forEach((section) => {
        const spaceIds = this.spaceIdsOf(section);
        if (previousSpaceIds.get(section.id) === spaceIds.join()) {
          return;
        }

        if (section.isFolder) {
          this.spaceFoldersService.setFolderSpaces(section.id, spaceIds);
          return;
        }

        this.spacesService
          .setPinnedSpaces(spaceIds)
          .pipe(
            catchError(() => of(null)),
            untilDestroyed(this),
          )
          .subscribe();
      });
  }

  private spaceIdsOf(section: SwitcherSection): string[] {
    return section.spaces.map((space) => space._id);
  }

  /** Folders sit between the last ordered section and the inbox, which is always last. */
  private indexOfInbox(sections: SwitcherSection[]): number {
    const index = sections.findIndex((section) => section.id === INBOX_SECTION_ID);
    return index === -1 ? sections.length : index;
  }

  private sectionComponentFor(sectionId: string): NavSidebarSectionComponent | undefined {
    return this.sectionComponents?.find((section) => section.sectionId === sectionId);
  }

  /**
   * Estimates how many rows fit below this component, so the first page roughly fills the sidebar
   * rather than trickling in a page at a time. Only an estimate — the lazy loader tops the list up
   * whenever it falls short.
   *
   * The spare rows carry the list past the lazy loader's lookahead: a page that ends exactly at the
   * fold leaves the early anchor on screen, and page 2 would load straight after page 1.
   */
  private calculatePageSize(): number {
    const availableHeight =
      window.innerHeight -
      this.hostRef.nativeElement.getBoundingClientRect().top -
      SECTION_HEADER_HEIGHT;
    return Math.max(
      SPARE_PAGE_ROWS,
      Math.floor(availableHeight / SWITCHER_ROW_HEIGHT) + SPARE_PAGE_ROWS,
    );
  }
}
